// Live's AI assistant tab (#242, spec docs/specs/editor/assistant.md; `editor-depth/SPEC.md` "AI
// assistant" and B6, live's `chat-panel.tsx` read for behaviour): the project's chat, oldest at the
// top, its older pages asked for at the top; the user's messages as bubbles, the assistant's
// answers as markdown (a plan's with Revert while this session may undo it), a plan's step rows
// as "✅" or "❌" and the step; "Thinking" while an answer is awaited; the chat box with the
// paperclip's Prompt Shortcuts and Send. With no message, live's greeting.
import { ActionIcon, Loader, Text, Textarea, Tooltip } from "@mantine/core";
import { ArrowUp, Paperclip, Undo2 } from "lucide-react";
import {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
  type UIEvent,
} from "react";

import type { AssistMessage } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { AssistText } from "./AssistText";
import { PromptShortcuts } from "./PromptShortcuts";
import type { AssistChat } from "./use-assist-chat";
import type { Assistant } from "./use-assistant";
import classes from "./assistant.module.css";

/** G11's longest message. */
const MAX_MESSAGE = 4000;

/** How near the top (px) a scroll asks for the older page, live's sentinel at the top. */
const OLDER_AT = 48;
/** How near the bottom (px) the list counts as read to the end, live's 100. */
const BOTTOM_WITHIN = 100;

export interface AssistantPanelProps {
  chat: AssistChat;
  /** Send, "Thinking", the line said and Revert. */
  assistant: Assistant;
  /** The member may not change this project: the chat is theirs to read only (G11). */
  readOnly: boolean;
}

export function AssistantPanel({ chat, assistant, readOnly }: AssistantPanelProps) {
  const { thinking } = assistant;
  const [input, setInput] = useState("");
  const [shortcuts, setShortcuts] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  // Live's: the box empties once the message is on its way.
  const send = async () => {
    if (readOnly) return;
    const text = input;
    if (await assistant.send(text)) setInput((current) => (current === text ? "" : current));
  };
  // The latest render's send behind one handler, so the chat box renders only for its own props.
  const latestSend = useRef(send);
  useLayoutEffect(() => {
    latestSend.current = send;
  });
  const onSend = useCallback(() => void latestSend.current(), []);
  const openShortcuts = useCallback(() => setShortcuts(true), []);
  if (chat.error !== null) {
    return (
      <div className={classes.panel}>
        <ErrorAlert error={chat.error} />
      </div>
    );
  }
  return (
    <div className={classes.panel}>
      {chat.loading ? (
        <div className={classes.loading}>
          <Loader size="sm" aria-label="Loading the chat" />
        </div>
      ) : chat.messages.length === 0 && !thinking ? (
        <Text className={classes.greeting}>
          Fresh project - Describe what you want to create or edit in the input below
        </Text>
      ) : (
        <Messages chat={chat} assistant={assistant} />
      )}
      <ChatBox
        box={box}
        value={input}
        onChange={setInput}
        readOnly={readOnly}
        thinking={thinking}
        onSend={onSend}
        onShortcuts={openShortcuts}
      />
      {assistant.said === null ? null : (
        <Text className={classes.said} data-tone={assistant.said.tone} role={assistant.said.tone === "error" ? "alert" : "status"}>
          {assistant.said.text}
        </Text>
      )}
      {readOnly ? (
        <Text className={classes.readOnly}>Only this project's creator or an admin can send messages here.</Text>
      ) : null}
      <PromptShortcuts
        opened={shortcuts}
        onClose={() => setShortcuts(false)}
        onPick={(prompt) => {
          // Live's pick replaces what is in the box.
          setInput(prompt);
          box.current?.focus();
        }}
      />
    </div>
  );
}

interface ChatBoxProps {
  box: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  /** An answer is awaited: nothing more is sent until it comes. */
  thinking: boolean;
  onSend: () => void;
  onShortcuts: () => void;
}

/**
 * The chat box and its tools. Its autosize measures the box at each of its renders, a read of the
 * box's computed style, so it renders only when its own props change, never at each render of the
 * editor around it (spec two-tabs-test-speed).
 */
const ChatBox = memo(function ChatBox({ box, value, onChange, readOnly, thinking, onSend, onShortcuts }: ChatBoxProps) {
  return (
    <div className={classes.chatBox}>
      <Textarea
        ref={box}
        aria-label="How can I help you edit?"
        placeholder="How can I help you edit?"
        variant="unstyled"
        disabled={readOnly}
        autosize
        minRows={3}
        maxRows={8}
        maxLength={MAX_MESSAGE}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
          // Live's: Enter sends, Shift+Enter starts a line.
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            onSend();
          }
        }}
        className={classes.chatInput}
      />
      <div className={classes.chatTools}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size={28}
          aria-label="Prompt shortcuts"
          disabled={readOnly}
          onClick={onShortcuts}
        >
          <Paperclip size={16} />
        </ActionIcon>
        <ActionIcon
          radius="xl"
          size={24}
          className={classes.send}
          aria-label="Send"
          disabled={readOnly || thinking || value.trim() === ""}
          onClick={onSend}
        >
          <ArrowUp size={14} />
        </ActionIcon>
      </div>
    </div>
  );
});

/**
 * The chat, oldest at the top. It opens at its end; a scroll near the top asks for the older page,
 * which goes above without moving what is shown; a list too short to scroll asks for older pages
 * until it fills the box or there are none (live's sentinel, seen at once). A new message at the
 * end is followed when the list was read to its end.
 */
function Messages({ chat, assistant }: { chat: AssistChat; assistant: Assistant }) {
  const { thinking } = assistant;
  const list = useRef<HTMLDivElement>(null);
  const seen = useRef<{ first: string | null; last: string | null; height: number; atBottom: boolean }>({
    first: null,
    last: null,
    height: 0,
    atBottom: true,
  });
  const first = chat.messages[0]?.id ?? null;
  const last = chat.messages.at(-1)?.id ?? null;
  useLayoutEffect(() => {
    const element = list.current;
    if (element === null) return;
    const was = seen.current;
    if (was.first !== null && first !== was.first && last === was.last) {
      // Older messages went above: keep the ones shown where they were.
      element.scrollTop += element.scrollHeight - was.height;
    } else if (was.atBottom) {
      element.scrollTop = element.scrollHeight;
    }
    seen.current = { first, last, height: element.scrollHeight, atBottom: was.atBottom };
    if (element.scrollHeight <= element.clientHeight) chat.loadOlder();
  }, [first, last, thinking, chat]);
  const scrolled = (event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    seen.current.atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + BOTTOM_WITHIN;
    seen.current.height = element.scrollHeight;
    if (element.scrollTop <= OLDER_AT) chat.loadOlder();
  };
  return (
    <div ref={list} className={classes.messages} onScroll={scrolled}>
      {chat.loadingOlder ? (
        <div className={classes.older}>
          <Dots />
          <span className={classes.hidden}>Loading older messages</span>
        </div>
      ) : null}
      <ol className={classes.rows} aria-label="Chat">
        {chat.messages.map((message) => (
          <Row
            key={message.id}
            message={message}
            onRevert={assistant.revertable.has(message.id) ? () => assistant.revert(message.id) : null}
          />
        ))}
      </ol>
      {thinking ? (
        <output className={classes.thinking}>
          <Dots />
          <span className={classes.thinkingLabel}>Thinking</span>
        </output>
      ) : null}
    </div>
  );
}

/**
 * One message: the user's as a bubble on the right; a plan's step row as its mark and the step, a
 * failed one's reason under it (its content, "Failed: {reason}"); an answer as markdown, with
 * Revert when it carried a plan this session may still undo.
 */
function Row({ message, onRevert }: { message: AssistMessage; onRevert: (() => void) | null }) {
  if (message.role === "user") {
    return (
      <li className={classes.user} data-role="user">
        {message.content}
      </li>
    );
  }
  if (message.step !== null) {
    const failed = message.step.status === "failed";
    return (
      <li className={classes.step} data-status={message.step.status}>
        <span>
          {failed ? "❌" : "✅"} {message.step.description}
        </span>
        {failed && message.content !== message.step.description ? (
          <span className={classes.stepReason}>{message.content}</span>
        ) : null}
      </li>
    );
  }
  return (
    <li className={classes.answer} data-role="assistant">
      <AssistText text={message.content} />
      {onRevert === null ? null : (
        <Tooltip label="Revert">
          <ActionIcon variant="subtle" color="gray" size={22} aria-label="Revert" onClick={onRevert}>
            <Undo2 size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </li>
  );
}

/** Live's three bouncing dots. */
function Dots() {
  return (
    <span className={classes.dots} aria-hidden>
      <span />
      <span />
      <span />
    </span>
  );
}
